import { de, type MessageKey as CoreKey } from './de.js';
import type { deLazy } from './deLazy.js';

/** Kernschlüssel plus die der getrennt geladenen Teile (nur Typ – die Texte kommen mit dem Teil, `deLazy.ts`) */
export type MessageKey = CoreKey | keyof typeof deLazy;
const messages: Record<string, string> = { ...de };
/** Texte eines getrennt geladenen Teils ergänzen */
export function addMessages(m: Record<string, string>) {
  Object.assign(messages, m);
}

/** Text holen; `{name}` wird durch die Werte ersetzt. */
export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  const raw = messages[key] ?? key;
  return vars ? raw.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? `{${k}}`)) : raw;
}

export const locale = 'de-DE';
export const formatDate = (iso: string) => new Date(iso).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });

import { daysBetween, todayIn, weekday } from '@werft/domain';

/** Heute als Kalendertag in der Zeitzone des Geräts */
const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
let cached = { day: '', until: 0 };
/** Heute als Kalendertag (P5-Messung: Intl-Formatierer je Zeile war teuer – 30 s zwischengespeichert) */
export const today = () => {
  const now = Date.now();
  if (now > cached.until) cached = { day: todayIn(zone), until: now + 30_000 };
  return cached.day;
};

const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

/** Termin kurz und menschlich: heute, morgen, Fr 30.10., 12.11., 03.02.2027 */
export function formatDue(due: string, now = today()): string {
  const diff = daysBetween(now, due);
  if (diff === 0) return t('due.today');
  if (diff === 1) return t('due.tomorrow');
  if (diff === -1) return t('due.yesterday');
  const [y, m, d] = due.split('-');
  const short = `${d}.${m}.`;
  if (diff > 1 && diff <= 6) return `${WEEKDAYS[weekday(due)]} ${short}`;
  return y === now.slice(0, 4) ? short : `${short}${y}`;
}

/** Zeitpunkt kurz: gerade eben, vor 5 Min., vor 2 Std., gestern 14:03, Fr 30.10. 09:12, 12.11.2025 */
export function formatWhen(iso: string, now = Date.now()): string {
  const d = new Date(iso);
  const diff = Math.max(0, now - d.getTime());
  if (diff < 60_000) return t('time.now');
  if (diff < 3_600_000) return t('time.minutes', { n: Math.floor(diff / 60_000) });
  const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const day = (x: Date) => x.toLocaleDateString('en-CA');
  if (day(d) === day(new Date(now))) return diff < 6 * 3_600_000 ? t('time.hours', { n: Math.floor(diff / 3_600_000) }) : time;
  if (day(d) === day(new Date(now - 86_400_000))) return t('time.yesterday', { time });
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  return d.toLocaleDateString(locale, sameYear ? { weekday: 'short', day: '2-digit', month: '2-digit' } : { day: '2-digit', month: '2-digit', year: 'numeric' }) + (sameYear ? `, ${time}` : '');
}

/** Zeitpunkt in der Zukunft (Erinnerungen, P23): heute, 18:00 · morgen, 08:00 · Mo 02.11., 08:00 */
export function formatAt(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const day = (x: Date) => x.toLocaleDateString('en-CA');
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (day(d) === day(now)) return `${t('due.today')}, ${time}`;
  if (day(d) === day(tomorrow)) return `${t('due.tomorrow')}, ${time}`;
  const sameYear = d.getFullYear() === now.getFullYear();
  return `${d.toLocaleDateString(locale, sameYear ? { weekday: 'short', day: '2-digit', month: '2-digit' } : { day: '2-digit', month: '2-digit', year: 'numeric' })}, ${time}`;
}
